import React, { useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardCharts from '../DashboardCharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  TrendingUp, TrendingDown, Wallet, Calendar as CalendarIcon, Users,
  MapPin, Award, AlertCircle, Clock, ArrowUpRight, ArrowDownRight,
  ChevronRight, Activity, Target, Zap, Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { id: 'nakit', label: 'Nakit', icon: '💵', accent: 'emerald', gradient: 'from-emerald-500 to-green-600' },
  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦', accent: 'red', gradient: 'from-red-600 to-rose-700' },
  { id: 'bostanci', label: 'Bostancı', icon: '🏢', accent: 'amber', gradient: 'from-amber-600 to-orange-700' }
];

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatTLShort = (n) => {
  const v = parseFloat(n) || 0;
  if (Math.abs(v) >= 1000000) return `₺${(v / 1000000).toFixed(1).replace('.0', '')}M`;
  if (Math.abs(v) >= 1000) return `₺${(v / 1000).toFixed(1).replace('.0', '')}K`;
  return `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }); } catch { return d; }
};

const DashboardTab = ({
  incomes = [],
  expenses = [],
  reservations = [],
  customers = [],
  salons = [],
  referees = [],
  todayReservations = [],
  refereesTotalBalance = null,
  archiveMonths = [],
  selectedMonth,
  onMonthChange,
  currentUser = null,
  dataLoading = false,
  onNavigate
}) => {
  // ===== Hesaplar (Bank breakdown) =====
  const accountBreakdown = useMemo(() => {
    const breakdown = {};
    PAYMENT_METHODS.forEach(pm => {
      breakdown[pm.id] = { ...pm, income: 0, expense: 0, net: 0, txCount: 0 };
    });
    incomes.forEach(i => {
      const pm = i.payment_method || 'nakit';
      if (breakdown[pm]) {
        breakdown[pm].income += parseFloat(i.amount) || 0;
        breakdown[pm].txCount += 1;
      }
    });
    expenses.forEach(e => {
      const pm = e.payment_method || 'nakit';
      if (breakdown[pm]) {
        breakdown[pm].expense += parseFloat(e.amount) || 0;
        breakdown[pm].txCount += 1;
      }
    });
    Object.values(breakdown).forEach(b => { b.net = b.income - b.expense; });
    return Object.values(breakdown);
  }, [incomes, expenses]);

  // ===== Toplam KPI'lar =====
  const totalIncome = incomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalExpense = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const netProfit = totalIncome - totalExpense;

  // Bekleyen Tahsilat
  const customerDebt = customers.filter(c => (c.balance || 0) > 0).reduce((s, c) => s + (c.balance || 0), 0);
  const refereePending = refereesTotalBalance?.total_balance || 0;
  const totalPending = customerDebt + refereePending;

  // En çok borçlu müşteriler
  const topDebtors = useMemo(
    () => customers.filter(c => (c.balance || 0) > 0).sort((a, b) => (b.balance || 0) - (a.balance || 0)).slice(0, 5),
    [customers]
  );

  // En çok bekleyen hakemler — TÜM aylar (backend ile birebir tutarlı)
  const [pendingReferees, setPendingReferees] = useState([]);
  const [pendingTotals, setPendingTotals] = useState({ total_to_collect: 0, total_awaiting: 0 });
  const [pendingLoading, setPendingLoading] = useState(true);
  const pendingInFlight = React.useRef(false);

  const loadPendingReferees = useCallback(async () => {
    if (pendingInFlight.current) return;
    pendingInFlight.current = true;
    setPendingLoading(true);
    try {
      const res = await axios.get(`${API}/referees/pending-summary`);
      setPendingReferees(res.data?.referees || []);
      setPendingTotals({
        total_to_collect: res.data?.total_to_collect || 0,
        total_awaiting: res.data?.total_awaiting || 0
      });
    } catch (e) {
      setPendingReferees([]);
    } finally {
      pendingInFlight.current = false;
      setPendingLoading(false);
    }
  }, []);

  // Mount'ta bir kez çek; başarısız/boş kalırsa 3 sn sonra bir kez daha dener.
  useEffect(() => {
    loadPendingReferees();
    const retry = setTimeout(() => {
      setPendingReferees(prev => {
        if (prev.length === 0) loadPendingReferees();
        return prev;
      });
    }, 3000);
    return () => clearTimeout(retry);
  }, [loadPendingReferees]);

  // Rezervasyon/tahsilat verisi değişince tazele
  useEffect(() => {
    if (reservations.length > 0) loadPendingReferees();
  }, [reservations.length, loadPendingReferees]);

  const topPendingReferees = useMemo(
    () => pendingReferees
      .map(r => ({ ...r, pending: (r.to_collect || 0) + (r.awaiting || 0), count: (r.to_collect_count || 0) + (r.awaiting_count || 0) }))
      .filter(r => r.pending > 0)
      .sort((a, b) => b.pending - a.pending)
      .slice(0, 5),
    [pendingReferees]
  );

  // Son gelir işlemleri (5 adet)
  const recentIncomes = useMemo(
    () => [...incomes].sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date)).slice(0, 5),
    [incomes]
  );

  // Bugünün tarihi
  const todayLabel = new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  // Bu ay mı kontrolü
  const currentMonthStr = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const isCurrentMonth = selectedMonth === currentMonthStr;

  // Toplam kasa bakiyesi (3 hesabın net'lerinin toplamı)
  const totalKasaBalance = accountBreakdown.reduce((s, a) => s + a.net, 0);

  return (
    <div className="space-y-4" data-testid="dashboard-tab-root">
      {/* ============ HEADER (Corporate) ============ */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-xl p-4 md:p-5 text-white shadow-2xl relative overflow-hidden">
        {/* Decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl -ml-24 -mb-24" />

        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-xl backdrop-blur border border-white/20">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">İşletme Genel Bakış</h1>
              <p className="text-xs text-slate-300 mt-0.5">{todayLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-3 py-2 border border-white/10">
              <CalendarIcon className="w-4 h-4 text-slate-300" />
              <select
                value={selectedMonth}
                onChange={(e) => onMonthChange?.(e.target.value)}
                className="bg-transparent text-white text-sm font-semibold outline-none cursor-pointer"
                data-testid="dashboard-month-selector"
              >
                {archiveMonths.length > 0 ? archiveMonths.map(m => (
                  <option key={m.month} value={m.month} className="text-slate-900 bg-white">
                    {m.label}{m.is_current ? ' (Bu Ay)' : ''}
                  </option>
                )) : (
                  <option value={selectedMonth} className="text-slate-900 bg-white">Bu Ay</option>
                )}
              </select>
            </div>
            <div className="hidden md:block px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Giriş</p>
              <p className="text-sm font-semibold">{currentUser?.username || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Arşiv Uyarısı */}
      {!isCurrentMonth && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3 flex items-center gap-3" data-testid="archive-warning">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-amber-900 font-semibold text-sm">
              Arşiv görünümü — {archiveMonths.find(m => m.month === selectedMonth)?.label || selectedMonth}
            </p>
            <p className="text-amber-700 text-xs">Geçmişe ait verileri inceliyorsunuz.</p>
          </div>
          <Button onClick={() => onMonthChange?.(currentMonthStr)} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="back-to-current-btn">
            Bu Aya Dön
          </Button>
        </div>
      )}

      {/* ============ KASA HESAPLARI - Banka Bakiyeleri ============ */}
      <div>
        <div className="flex items-center justify-between mb-2 px-1">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Building2 className="w-4 h-4 text-slate-500" /> Kasa Hesapları
          </h3>
          <p className="text-xs text-slate-500">
            Toplam Bakiye: <span className={`font-bold ${totalKasaBalance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatTL(totalKasaBalance)}</span>
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {accountBreakdown.map(acc => (
            <Card key={acc.id} className={`bg-gradient-to-br ${acc.gradient} text-white border-0 shadow-lg hover:shadow-xl transition-all relative overflow-hidden`} data-testid={`dashboard-account-${acc.id}`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-16 -mt-16" />
              <CardContent className="p-4 relative">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{acc.icon}</span>
                    <p className="font-semibold text-sm">{acc.label}</p>
                  </div>
                  <Badge className="bg-white/20 text-white border-0 text-[10px]">{acc.txCount} işlem</Badge>
                </div>
                <p className="text-2xl md:text-3xl font-bold mt-2">{formatTL(acc.net)}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/20 text-xs">
                  <div>
                    <p className="text-white/70 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Giriş</p>
                    <p className="font-bold">{formatTL(acc.income)}</p>
                  </div>
                  <div>
                    <p className="text-white/70 flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> Çıkış</p>
                    <p className="font-bold">{formatTL(acc.expense)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ============ KPI Kartları ============ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow" data-testid="dashboard-kpi-income">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Aylık Gelir</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{formatTL(totalIncome)}</p>
                <p className="text-xs text-slate-400 mt-1">{incomes.length} işlem</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500 hover:shadow-lg transition-shadow" data-testid="dashboard-kpi-expense">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Aylık Gider</p>
                <p className="text-2xl font-bold text-rose-700 mt-1">{formatTL(totalExpense)}</p>
                <p className="text-xs text-slate-400 mt-1">{expenses.length} işlem</p>
              </div>
              <div className="p-2 rounded-lg bg-rose-100"><TrendingDown className="w-5 h-5 text-rose-600" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${netProfit >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'} hover:shadow-lg transition-shadow`} data-testid="dashboard-kpi-net">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Net Kâr</p>
                <p className={`text-2xl font-bold mt-1 ${netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  {formatTL(netProfit)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Gelir − Gider</p>
              </div>
              <div className={`p-2 rounded-lg ${netProfit >= 0 ? 'bg-blue-100' : 'bg-orange-100'}`}>
                <Wallet className={`w-5 h-5 ${netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow" data-testid="dashboard-kpi-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Bekleyen Tahsilat</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{formatTL(totalPending)}</p>
                <p className="text-xs text-slate-400 mt-1">{topDebtors.length} müş. + {topPendingReferees.length} hakem</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100"><Clock className="w-5 h-5 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============ Mini Stats ============ */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Card className="hover:shadow transition-shadow cursor-pointer" onClick={() => onNavigate?.('reservations')}>
          <CardContent className="p-3 text-center">
            <CalendarIcon className="w-5 h-5 mx-auto mb-1 text-blue-600" />
            <p className="text-xl font-bold text-slate-800">{todayReservations.length}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Bugünkü Rez.</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow transition-shadow cursor-pointer" onClick={() => onNavigate?.('customers')}>
          <CardContent className="p-3 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-xl font-bold text-slate-800">{customers.length}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Müşteri</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow transition-shadow cursor-pointer" onClick={() => onNavigate?.('salons')}>
          <CardContent className="p-3 text-center">
            <MapPin className="w-5 h-5 mx-auto mb-1 text-purple-600" />
            <p className="text-xl font-bold text-slate-800">{salons.length}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Salon</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow transition-shadow cursor-pointer" onClick={() => onNavigate?.('referees')}>
          <CardContent className="p-3 text-center">
            <Award className="w-5 h-5 mx-auto mb-1 text-amber-600" />
            <p className="text-xl font-bold text-slate-800">{referees.length}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Hakem</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow transition-shadow cursor-pointer" onClick={() => onNavigate?.('financials')}>
          <CardContent className="p-3 text-center">
            <Target className="w-5 h-5 mx-auto mb-1 text-rose-600" />
            <p className="text-xl font-bold text-slate-800">{topDebtors.length}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Borçlu</p>
          </CardContent>
        </Card>
      </div>

      {/* ============ Bugünkü Rezervasyonlar + Son Gelirler ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Bugünkü Rezervasyonlar */}
        <Card>
          <CardHeader className="border-b py-3 bg-blue-50/50">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2 text-blue-900">
                <CalendarIcon className="w-4 h-4" /> Bugünkü Rezervasyonlar
              </span>
              <Badge className="bg-blue-600 hover:bg-blue-600">{todayReservations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[360px] overflow-y-auto">
            {todayReservations.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm" data-testid="today-reservations-empty">{dataLoading ? 'Yükleniyor...' : 'Bugün rezervasyon yok'}</p>
              </div>
            ) : (
              <div className="divide-y">
                {todayReservations.slice(0, 8).map(r => {
                  const ref = referees.find(x => x.id === r.referee_id);
                  const customer = customers.find(c => c.id === r.customer_id);
                  const salon = salons.find(s => s.id === r.salon_id);
                  return (
                    <div key={r.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors" data-testid={`today-reservation-${r.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs shadow">
                          {(r.start_time || '').slice(0, 5)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{customer?.name || 'Müşteri'}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {salon?.name || 'Salon'}
                            {ref && <span className="text-amber-600 ml-1">• 🏅 {ref.name}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-bold text-slate-800 text-sm">{formatTL(r.total_amount)}</p>
                        <Badge className={`text-[10px] mt-0.5 ${r.payment_status === 'odendi' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-rose-100 text-rose-700 hover:bg-rose-100'} border-0`}>
                          {r.payment_status === 'odendi' ? 'Ödendi' : 'Ödenmedi'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
                {todayReservations.length > 8 && (
                  <div className="p-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => onNavigate?.('reservations')} className="text-xs">
                      +{todayReservations.length - 8} daha — Tümünü Gör <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Son Gelir Hareketleri */}
        <Card>
          <CardHeader className="border-b py-3 bg-emerald-50/50">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2 text-emerald-900">
                <ArrowUpRight className="w-4 h-4" /> Son Gelir Hareketleri
              </span>
              <Button variant="ghost" size="sm" onClick={() => onNavigate?.('financials')} className="text-xs h-6 text-emerald-700">
                Tümü <ChevronRight className="w-3 h-3 ml-0.5" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[360px] overflow-y-auto">
            {recentIncomes.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <ArrowUpRight className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm" data-testid="recent-incomes-empty">{dataLoading ? 'Yükleniyor...' : 'Henüz gelir kaydı yok'}</p>
              </div>
            ) : (
              <div className="divide-y">
                {recentIncomes.map(income => {
                  const pm = PAYMENT_METHODS.find(p => p.id === (income.payment_method || 'nakit')) || PAYMENT_METHODS[0];
                  return (
                    <div key={income.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-lg flex-shrink-0">
                          {pm.icon}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{income.title}</p>
                          <p className="text-xs text-slate-500">{formatDate(income.date)} • {pm.label}</p>
                        </div>
                      </div>
                      <p className="font-bold text-emerald-600 text-sm flex-shrink-0 ml-2">+{formatTLShort(income.amount)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ============ Grafikler: Gelir/Gider & Tahsilat Trendi ============ */}
      <DashboardCharts />

      {/* ============ Bekleyen Tahsilatlar Detayı ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Top Borçlu Müşteriler */}
        <Card>
          <CardHeader className="border-b py-3 bg-amber-50/50">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2 text-amber-900">
                <Users className="w-4 h-4" /> Borçlu Müşteriler (Top 5)
              </span>
              <p className="text-sm font-bold text-amber-800">{formatTL(customerDebt)}</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {topDebtors.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p data-testid="debtors-empty">{dataLoading ? 'Yükleniyor...' : 'Borçlu müşteri yok 🎉'}</p>
              </div>
            ) : (
              <div className="divide-y">
                {topDebtors.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 hover:bg-amber-50 cursor-pointer transition-colors" onClick={() => onNavigate?.('customers', { customerId: c.id })} data-testid={`dashboard-debtor-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-semibold text-xs flex items-center justify-center flex-shrink-0">
                        {(c.name || '').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{c.name}</p>
                        <p className="text-xs text-slate-500 truncate">{c.phone || 'Telefon yok'}</p>
                      </div>
                    </div>
                    <p className="font-bold text-amber-700 text-sm flex-shrink-0 ml-2">{formatTL(c.balance)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Bekleyen Hakem Tahsilatları */}
        <Card>
          <CardHeader className="border-b py-3 bg-orange-50/50">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2 text-orange-900">
                <Award className="w-4 h-4" /> Hakemden Bekleyen (Top 5)
              </span>
              <p className="text-sm font-bold text-orange-800">{formatTL(pendingTotals.total_to_collect + pendingTotals.total_awaiting)}</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pendingLoading && topPendingReferees.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm" data-testid="pending-referees-loading">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-30 animate-pulse" />
                <p>Hakem tahsilatları yükleniyor...</p>
              </div>
            ) : topPendingReferees.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Award className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Hakem tahsilatı yok ✓</p>
              </div>
            ) : (
              <div className="divide-y">
                {topPendingReferees.map(ref => (
                  <div key={ref.referee_id} className="flex items-center justify-between p-3 hover:bg-orange-50 cursor-pointer transition-colors" onClick={() => onNavigate?.('referees', { refereeId: ref.referee_id })} data-testid={`dashboard-pending-referee-${ref.referee_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white font-semibold text-xs flex items-center justify-center flex-shrink-0">
                        {(ref.name || '').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{ref.name}</p>
                        <p className="text-xs text-slate-500">
                          {ref.count} bekleyen maç
                          {(ref.to_collect || 0) > 0 && <span className="text-orange-600"> • {formatTLShort(ref.to_collect)} teslim edecek</span>}
                          {(ref.awaiting || 0) > 0 && <span className="text-slate-400"> • {formatTLShort(ref.awaiting)} müşteri ödemedi</span>}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-orange-700 text-sm flex-shrink-0 ml-2">{formatTL(ref.pending)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ============ Quick Actions ============ */}
      <Card className="bg-gradient-to-r from-slate-50 to-blue-50">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-blue-600" />
            <p className="text-sm font-semibold text-slate-700">Hızlı Erişim</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button variant="outline" className="h-14 flex-col gap-1 hover:bg-blue-50 hover:border-blue-300" onClick={() => onNavigate?.('reservations')} data-testid="quick-reservation-btn">
              <CalendarIcon className="w-4 h-4 text-blue-600" />
              <span className="text-xs">Yeni Rezervasyon</span>
            </Button>
            <Button variant="outline" className="h-14 flex-col gap-1 hover:bg-emerald-50 hover:border-emerald-300" onClick={() => onNavigate?.('customers')} data-testid="quick-customer-btn">
              <Users className="w-4 h-4 text-emerald-600" />
              <span className="text-xs">Müşteri Ekle</span>
            </Button>
            <Button variant="outline" className="h-14 flex-col gap-1 hover:bg-purple-50 hover:border-purple-300" onClick={() => onNavigate?.('financials')} data-testid="quick-finance-btn">
              <Wallet className="w-4 h-4 text-purple-600" />
              <span className="text-xs">Kasa İşlemi</span>
            </Button>
            <Button variant="outline" className="h-14 flex-col gap-1 hover:bg-amber-50 hover:border-amber-300" onClick={() => onNavigate?.('referees')} data-testid="quick-referee-btn">
              <Award className="w-4 h-4 text-amber-600" />
              <span className="text-xs">Hakem Tahsilat</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardTab;
